import React, { useRef, useMemo } from 'react';
import Select, { components } from 'react-select';
import { baseClasses as checkboxStyles } from 'src/ui/components/Checkbox';
import { inputLabelClasses } from 'src/ui/components/TextField';
import { MultiSelectInputType } from 'src/features/CallDocuments/types/callDocumentList';

type MultiSelectCheckBoxType = {
  options: Array<MultiSelectInputType>;
  setSelectedOptions?: (values: Array<MultiSelectInputType>) => void;
  selectedOptions?: Array<MultiSelectInputType> | undefined;
  size?: string;
  label?: string;
  id?: string;
  isSearchable?: boolean;
  position?: string;
  index?: boolean;
};

function MultiSelectCheckBox({
  options,
  setSelectedOptions,
  selectedOptions,
  size,
  label,
  id,
  isSearchable,
  position,
  index,
}: MultiSelectCheckBoxType) {
  const useRes = useRef<any>();
  const { Option } = components;

  const hasSelectAll = useMemo(
    () => options.some(o => o.value === 0),
    [options],
  );

  // listing checkbox along with dropdown list
  const CheckboxOption = ({ children, ...props }: any) => (
    <Option {...props}>
      <div className="flex items-start space-x-2">
        <input
          type="checkbox"
          checked={props?.isSelected}
          className={`${checkboxStyles} h-5 w-5`}
          onChange={() => {}}
        />
        <label
          className={`${inputLabelClasses} leading-tight`}
          style={{ overflowWrap: 'anywhere' }}
        >
          {children}
        </label>
      </div>
    </Option>
  );

  const onChange = (value: any, event: any) => {
    if (!setSelectedOptions) return;

    if (hasSelectAll) {
      if (event.action === 'select-option' && event.option.value === 0) {
        setSelectedOptions([...options]);
        return;
      } else if (
        event.action === 'deselect-option' &&
        event.option.value === 0
      ) {
        setSelectedOptions([]);
        return;
      }
    }

    const currentSelectedWithoutSelectAll = value.filter(
      (o: any) => o.value !== 0,
    );
    const optionsWithoutSelectAll = options.filter(o => o.value !== 0);

    if (event.action === 'deselect-option') {
      setSelectedOptions(currentSelectedWithoutSelectAll);
      return;
    }

    if (hasSelectAll) {
      const allSelectedExcludingPlaceholder =
        currentSelectedWithoutSelectAll.length ===
          optionsWithoutSelectAll.length && optionsWithoutSelectAll.length > 0;

      if (allSelectedExcludingPlaceholder) {
        const selectAllOption = options.find(o => o.value === 0);
        if (selectAllOption) {
          setSelectedOptions([
            selectAllOption,
            ...currentSelectedWithoutSelectAll,
          ]);
          return;
        }
      }
    }

    setSelectedOptions(currentSelectedWithoutSelectAll);
  };

  // placeholder text
  const selected = useMemo(() => {
    return selectedOptions?.length === options?.length
      ? 'Select(All)'
      : `Select(${selectedOptions?.length ? selectedOptions?.length : '0'})`;
  }, [selectedOptions?.length, options?.length]);

  return (
    <Select
      id={id}
      ref={useRes}
      options={options}
      isMulti
      // menuIsOpen={true}
      hideSelectedOptions={false}
      components={{ Option: CheckboxOption }}
      controlShouldRenderValue={false}
      value={selectedOptions}
      closeMenuOnSelect={false}
      blurInputOnSelect={false}
      onChange={onChange}
      openMenuOnClick={true}
      placeholder={selected}
      isSearchable={isSearchable ?? false}
      classNamePrefix="multiselect-dropdown"
      className={`${
        size === 'extra-small'
          ? 'multiselect-dropdown multiselect-dropdown--extrasmall'
          : size === 'small'
          ? 'multiselect-dropdown multiselect-dropdown--small'
          : position === 'right'
          ? 'multiselect-dropdown multiselect-dropdown--right'
          : 'multiselect-dropdown'
      }`}
      styles={
        index
          ? {
              menuPortal: base => ({ ...base, zIndex: 3 }),
            }
          : undefined
      }
      aria-label={label}
      menuPlacement="auto"
      menuPosition="fixed"
    />
  );
}

export { MultiSelectCheckBox };
